/**
 * DS-aware Dagre layout for the KG canvas.
 *
 * Why this exists (vs. plain Dagre / ELK):
 *   • Tables are packed into a compact grid scoped to their owning Schema (or
 *     Data Source when no schema exists) using a hybrid row-first expansion.
 *   • Schema nodes sit in their own column and are vertically centered on the
 *     table sub-grid they own. Data Source nodes are centered on their whole
 *     block. This mirrors the product "instances → schemas → tables" reading.
 *   • Every other layer (dataModel, ontology, metric, report, dataStory,
 *     artifact) wraps into additional columns once it would overflow the
 *     viewport height, so tall layers stay visible instead of scrolling off.
 *
 * A lightweight barycenter pass derives a low-crossing vertical ordering within
 * each layer (no external graph-layout dependency); final x/y are then assigned
 * by the custom logic below.
 *
 * USAGE:
 *   import { getDagreLayout } from "./utils/dagreLayout";
 *   const nodes = getDagreLayout(rawNodes, rawEdges, { viewportHeight: 820 });
 *
 * To preview this layout with offline sample data set
 *   REACT_APP_KG_GRAPH_API=mock
 * which serves `sampleNodes` / `sampleEdges` (already schema-aware).
 */

import {
  KG_DATA_STORY_NODE_WIDTH_PX,
  KG_METRIC_NODE_HEIGHT_PX,
  KG_NODE_HEIGHT_PX,
  KG_ONTOLOGY_NODE_SIZE_PX,
  KG_REPORT_NODE_HEIGHT_PX,
  KG_REPORT_NODE_HEIGHT_WITH_FOOTER_PX,
  KG_REPORT_NODE_WIDTH_PX,
  KG_SQUARE_NODE_SIZE_PX,
  KG_TABLE_FIELD_ROW_HEIGHT_PX,
  KG_TABLE_MORE_FOOTER_HEIGHT_PX,
  KG_TABLE_NODE_WIDTH_PX,
} from "../../kgNodeDimensions";
import type { KgFlowEdge, KgFlowNode, KgFlowNodeType } from "../types";
import { getKgNodeTier } from "./kgTierPartition";

// ── Tunable constants ─────────────────────────────────────────────────────────

/** Outer canvas inset before the first node. */
export const MARGIN = 24;
/** Horizontal gap between table columns inside a block. */
export const COL_GAP = 12;
/** Vertical gap between table rows and stacked downstream nodes. */
export const ROW_GAP = 4;
/** Vertical gap between consecutive Data Source blocks. */
export const BLOCK_GAP = 8;
/** Vertical gap between schema sub-blocks inside one Data Source block. */
export const SCHEMA_BLOCK_GAP = 4;
/** Horizontal gap between layer columns. */
export const LAYER_GAP = 100;
/** Table card header row (icon + title + divider) — layout estimate only. */
const TABLE_HEADER_HEIGHT_PX = 36;
/** Used when the live canvas height has not been measured yet. */
export const DEFAULT_VIEWPORT_HEIGHT = 800;

/** Layers placed (and wrapped) after the DS → Schema → Table cluster. */
const DOWNSTREAM_TYPES: KgFlowNodeType[] = [
  "dataModel",
  "ontology",
  "metric",
  "report",
  "dataStory",
  "artifact",
];

// ── Grid math (row-first hybrid expansion) ────────────────────────────────────

interface GridDimensions {
  rows: number;
  cols: number;
}

/**
 * Smallest {rows, cols} grid that fits `n` nodes. Stays at the base grid until
 * it overflows, then grows rows first, then cols, alternating.
 */
const computeGridDimensions = (
  n: number,
  baseRows: number,
  baseCols: number,
): GridDimensions => {
  if (n <= baseRows * baseCols) return { rows: baseRows, cols: baseCols };
  let rows = baseRows;
  let cols = baseCols;
  let next: "row" | "col" = "row";
  while (n > rows * cols) {
    if (next === "row") {
      rows += 1;
      next = "col";
    } else {
      cols += 1;
      next = "row";
    }
  }
  return { rows, cols };
};

/** Row-major position of index `i` within a grid of `cols` columns. */
const blockPos = (i: number, cols: number): { col: number; row: number } => ({
  col: i % cols,
  row: Math.floor(i / cols),
});

/**
 * Rows that actually hold nodes (≤15 still places on a 3×5 grid, but block
 * height only spans occupied rows — no empty reserved rows below content).
 */
const usedRowsInBlock = (
  n: number,
  baseRows: number,
  baseCols: number,
): number => {
  if (n <= 0) return 0;
  if (n <= baseRows * baseCols) {
    return Math.min(baseRows, Math.ceil(n / baseCols));
  }
  return computeGridDimensions(n, baseRows, baseCols).rows;
};

/** Pixel height of a grid block holding `n` nodes of `rowH` height. */
const blockHeightFor = (
  n: number,
  rowH: number,
  baseRows: number,
  baseCols: number,
): number => {
  const rows = usedRowsInBlock(n, baseRows, baseCols);
  if (rows <= 0) return 0;
  return rows * (rowH + ROW_GAP) - ROW_GAP;
};

// ── Node sizing ───────────────────────────────────────────────────────────────

interface NodeSize {
  width: number;
  height: number;
}

/**
 * Compact table height for layout — matches the default canvas card (3 visible
 * fields, "+ N more" footer when applicable). Avoids inflating grid rows with
 * the old 200px fallback or post-measure expanded heights.
 */
const estimateTableLayoutHeight = (node: KgFlowNode): number => {
  const fieldCount = node.data?.fields?.length ?? 3;
  const visibleCount = Math.min(fieldCount, 3);
  const hasMoreFooter = fieldCount > 3;
  return (
    TABLE_HEADER_HEIGHT_PX +
    visibleCount * KG_TABLE_FIELD_ROW_HEIGHT_PX +
    (hasMoreFooter ? KG_TABLE_MORE_FOOTER_HEIGHT_PX : 0)
  );
};

const resolveNodeDimensions = (
  node: KgFlowNode,
  defaults: NodeSize,
): NodeSize => {
  if (node.type === "dataSource" || node.type === "schema") {
    return { width: KG_SQUARE_NODE_SIZE_PX, height: KG_SQUARE_NODE_SIZE_PX };
  }

  if (node.type === "report") {
    const hasFooter = Boolean(node.data?.biName || node.data?.biLogoUrl);
    return {
      width: node.measured?.width ?? KG_REPORT_NODE_WIDTH_PX,
      height:
        node.measured?.height ??
        node.height ??
        (hasFooter
          ? KG_REPORT_NODE_HEIGHT_WITH_FOOTER_PX
          : KG_REPORT_NODE_HEIGHT_PX),
    };
  }

  if (node.type === "dataStory") {
    return {
      width: node.measured?.width ?? KG_DATA_STORY_NODE_WIDTH_PX,
      height: node.measured?.height ?? node.height ?? defaults.height,
    };
  }

  if (node.type === "table") {
    return {
      width: node.measured?.width ?? node.width ?? KG_TABLE_NODE_WIDTH_PX,
      height: estimateTableLayoutHeight(node),
    };
  }

  return {
    width: node.measured?.width ?? node.width ?? defaults.width,
    height: node.measured?.height ?? node.height ?? defaults.height,
  };
};

/** Compact layout height per downstream node type (avoids 140px generic fallback). */
const downstreamLayoutHeight = (node: KgFlowNode): number => {
  switch (node.type) {
    case "ontology":
      return KG_ONTOLOGY_NODE_SIZE_PX;
    case "metric":
      return KG_METRIC_NODE_HEIGHT_PX;
    case "report":
      return resolveNodeDimensions(node, {
        width: KG_TABLE_NODE_WIDTH_PX,
        height: KG_NODE_HEIGHT_PX,
      }).height;
    case "dataStory":
      return dimsSafeHeight(node, KG_NODE_HEIGHT_PX);
    case "artifact":
      return KG_METRIC_NODE_HEIGHT_PX;
    case "dataModel":
      return KG_NODE_HEIGHT_PX;
    default:
      return KG_NODE_HEIGHT_PX;
  }
};

const dimsSafeHeight = (node: KgFlowNode, fallback: number): number =>
  node.measured?.height ?? node.height ?? fallback;

// ── Public API ────────────────────────────────────────────────────────────────

export type DagreDirection = "LR" | "TB";

export interface GetDagreLayoutOptions {
  direction?: DagreDirection;
  /** Live canvas height in px — drives column wrapping for non-table layers. */
  viewportHeight?: number;
  /** Base table grid rows before overflow expansion. */
  baseRows?: number;
  /** Base table grid cols before overflow expansion. */
  baseCols?: number;
}

/**
 * Returns the input nodes with `position`, `sourcePosition`, `targetPosition`
 * set. Pure: never mutates the inputs.
 */
export const getDagreLayout = (
  nodes: KgFlowNode[],
  edges: KgFlowEdge[],
  opts: GetDagreLayoutOptions = {},
): KgFlowNode[] => {
  const direction = opts.direction ?? "LR";
  const baseRows = Math.max(1, opts.baseRows ?? 3);
  const baseCols = Math.max(1, opts.baseCols ?? 5);
  const viewportHeight =
    opts.viewportHeight && opts.viewportHeight > 0
      ? opts.viewportHeight
      : DEFAULT_VIEWPORT_HEIGHT;

  const sourcePosition = direction === "LR" ? "right" : "bottom";
  const targetPosition = direction === "LR" ? "left" : "top";

  const dims = new Map<string, NodeSize>();
  nodes.forEach((node) =>
    dims.set(
      node.id,
      resolveNodeDimensions(node, {
        width: KG_TABLE_NODE_WIDTH_PX,
        height: KG_NODE_HEIGHT_PX,
      }),
    ),
  );

  // ── Adjacency (source → [targets]) and parents (target → [sources]) ────────
  const adjacency = new Map<string, string[]>();
  const parentsByNode = new Map<string, string[]>();
  edges.forEach((edge) => {
    if (!edge.source || !edge.target) return;
    const children = adjacency.get(edge.source);
    if (children) children.push(edge.target);
    else adjacency.set(edge.source, [edge.target]);
    const parents = parentsByNode.get(edge.target);
    if (parents) parents.push(edge.source);
    else parentsByNode.set(edge.target, [edge.source]);
  });
  const childrenOf = (id: string): string[] => adjacency.get(id) ?? [];

  // ── Vertical ordering: self-contained barycenter pass ──────────────────────
  // Process tiers left→right; each layer is ordered by the average position of
  // its already-placed parents (barycenter), which keeps connected nodes close
  // and reduces crossings — without an external graph-layout dependency.
  const TIER_STRIDE = 100000;
  const FALLBACK_TIER = 999;
  const orderRank = new Map<string, number>();
  {
    const inputIndex = new Map<string, number>();
    nodes.forEach((node, index) => inputIndex.set(node.id, index));

    const tierToNodes = new Map<number, KgFlowNode[]>();
    nodes.forEach((node) => {
      const tier = getKgNodeTier(node.type) ?? FALLBACK_TIER;
      const bucket = tierToNodes.get(tier);
      if (bucket) bucket.push(node);
      else tierToNodes.set(tier, [node]);
    });
    const tiers = [...tierToNodes.keys()].sort((a, b) => a - b);

    const indexInLayer = new Map<string, number>();
    let isFirstLayer = true;
    tiers.forEach((tier) => {
      const layerNodes = tierToNodes.get(tier) ?? [];
      let ordered: KgFlowNode[];
      if (isFirstLayer) {
        ordered = [...layerNodes].sort(
          (a, b) => (inputIndex.get(a.id) ?? 0) - (inputIndex.get(b.id) ?? 0),
        );
        isFirstLayer = false;
      } else {
        const barycenter = (node: KgFlowNode): number => {
          const known = (parentsByNode.get(node.id) ?? [])
            .map((parentId) => indexInLayer.get(parentId))
            .filter((value): value is number => value !== undefined);
          if (known.length === 0) {
            // Unparented nodes sort after parented ones, stable by input order.
            return TIER_STRIDE + (inputIndex.get(node.id) ?? 0);
          }
          return known.reduce((sum, value) => sum + value, 0) / known.length;
        };
        ordered = [...layerNodes]
          .map((node, index) => ({ node, key: barycenter(node), index }))
          .sort((a, b) => (a.key === b.key ? a.index - b.index : a.key - b.key))
          .map((entry) => entry.node);
      }
      ordered.forEach((node, index) => {
        indexInLayer.set(node.id, index);
        orderRank.set(node.id, tier * TIER_STRIDE + index);
      });
    });
  }

  const orderByRank = (list: KgFlowNode[]): KgFlowNode[] =>
    [...list].sort(
      (a, b) => (orderRank.get(a.id) ?? 0) - (orderRank.get(b.id) ?? 0),
    );

  // ── Classify nodes ─────────────────────────────────────────────────────────
  const byType = (type: KgFlowNodeType): KgFlowNode[] =>
    nodes.filter((node) => node.type === type);

  const dsNodes = orderByRank(byType("dataSource"));
  const schemaNodes = byType("schema");
  const tableNodes = byType("table");
  const tableIdSet = new Set(tableNodes.map((n) => n.id));
  const tableById = new Map(tableNodes.map((n) => [n.id, n]));

  // Schemas grouped under their owning Data Source (DS → schema edge).
  const schemasByDs = new Map<string, KgFlowNode[]>();
  dsNodes.forEach((ds) => schemasByDs.set(ds.id, []));
  schemaNodes.forEach((schema) => {
    const parent = dsNodes.find((ds) => childrenOf(ds.id).includes(schema.id));
    if (parent) schemasByDs.get(parent.id)?.push(schema);
  });
  dsNodes.forEach((ds) =>
    schemasByDs.set(ds.id, orderByRank(schemasByDs.get(ds.id) ?? [])),
  );

  // Tables grouped under their schema (schema → table edge) — first owner wins.
  const assignedTableIds = new Set<string>();
  const tablesBySchema = new Map<string, KgFlowNode[]>();
  schemaNodes.forEach((schema) => {
    const owned: KgFlowNode[] = [];
    childrenOf(schema.id).forEach((targetId) => {
      const table = tableById.get(targetId);
      if (table && tableIdSet.has(targetId) && !assignedTableIds.has(targetId)) {
        owned.push(table);
        assignedTableIds.add(targetId);
      }
    });
    tablesBySchema.set(schema.id, orderByRank(owned));
  });

  // Tables connected directly to a Data Source (schema-less branch).
  const tablesByDs = new Map<string, KgFlowNode[]>();
  dsNodes.forEach((ds) => {
    const owned: KgFlowNode[] = [];
    childrenOf(ds.id).forEach((targetId) => {
      const table = tableById.get(targetId);
      if (table && tableIdSet.has(targetId) && !assignedTableIds.has(targetId)) {
        owned.push(table);
        assignedTableIds.add(targetId);
      }
    });
    tablesByDs.set(ds.id, orderByRank(owned));
  });

  const unassignedTables = orderByRank(
    tableNodes.filter((table) => !assignedTableIds.has(table.id)),
  );

  // Uniform grid row stride — compact default card, not max measured height.
  const tableRowH = estimateTableLayoutHeight({
    type: "table",
    id: "__grid-stride__",
    position: { x: 0, y: 0 },
    data: { fields: [{ name: "a" }, { name: "b" }, { name: "c" }] },
  });
  const tableColW = KG_TABLE_NODE_WIDTH_PX + COL_GAP;

  // ── Column X positions for the DS → Schema → Table cluster ─────────────────
  const square = KG_SQUARE_NODE_SIZE_PX;
  const dsX = MARGIN;
  const hasSchemaColumn = schemaNodes.length > 0;
  const schemaX = dsX + square + LAYER_GAP;
  const tableX = hasSchemaColumn
    ? schemaX + square + LAYER_GAP
    : dsX + square + LAYER_GAP;

  const positions = new Map<string, { x: number; y: number }>();
  let maxTableCols = 0;

  /** Places a grid of tables starting at `blockStartY`, returns block height. */
  const placeTableGrid = (tables: KgFlowNode[], blockStartY: number): number => {
    if (!tables.length) return 0;
    const { cols } = computeGridDimensions(tables.length, baseRows, baseCols);
    maxTableCols = Math.max(maxTableCols, cols);
    tables.forEach((table, index) => {
      const { col, row } = blockPos(index, cols);
      positions.set(table.id, {
        x: tableX + col * tableColW,
        y: blockStartY + row * (tableRowH + ROW_GAP),
      });
    });
    return blockHeightFor(tables.length, tableRowH, baseRows, baseCols);
  };

  // ── Walk Data Source blocks top → bottom ───────────────────────────────────
  let runningY = MARGIN;

  dsNodes.forEach((ds) => {
    const schemas = schemasByDs.get(ds.id) ?? [];
    const directTables = tablesByDs.get(ds.id) ?? [];
    const blockStartY = runningY;

    if (schemas.length > 0) {
      let cursorY = blockStartY;
      schemas.forEach((schema, schemaIndex) => {
        const schemaTables = tablesBySchema.get(schema.id) ?? [];
        const subHeight =
          schemaTables.length > 0
            ? placeTableGrid(schemaTables, cursorY)
            : square;
        positions.set(schema.id, {
          x: schemaX,
          y: cursorY + subHeight / 2 - square / 2,
        });
        cursorY +=
          subHeight + (schemaIndex < schemas.length - 1 ? SCHEMA_BLOCK_GAP : 0);
      });
      // A DS may also own tables directly (no schema in between).
      if (directTables.length > 0) {
        cursorY += SCHEMA_BLOCK_GAP;
        cursorY += placeTableGrid(directTables, cursorY);
      }
      const blockHeight = Math.max(cursorY - blockStartY, square);
      positions.set(ds.id, {
        x: dsX,
        y: blockStartY + blockHeight / 2 - square / 2,
      });
      runningY = blockStartY + blockHeight + BLOCK_GAP;
      return;
    }

    const blockHeight = Math.max(
      placeTableGrid(directTables, blockStartY),
      square,
    );
    positions.set(ds.id, {
      x: dsX,
      y: blockStartY + blockHeight / 2 - square / 2,
    });
    runningY = blockStartY + blockHeight + BLOCK_GAP;
  });

  // Orphan schemas (no DS parent) — simple vertical stack in the schema column.
  schemaNodes
    .filter((schema) => !positions.has(schema.id))
    .forEach((schema) => {
      positions.set(schema.id, { x: schemaX, y: runningY });
      runningY += square + ROW_GAP;
    });

  // Unassigned tables — trailing grid block.
  if (unassignedTables.length) {
    const height = placeTableGrid(unassignedTables, runningY);
    runningY += height + BLOCK_GAP;
  }

  // ── Running X cursor for downstream layers ─────────────────────────────────
  // Ontology / metric / report … columns are TOP-aligned with the DS cluster
  // (same Y origin as the first data-source row), not vertically centred on the
  // full cluster height — centring pushed them to the bottom when many DS blocks
  // are stacked (see reference layout in the DS-block-grid experiment).
  const downstreamStartY = MARGIN;
  let cursorX: number;
  if (tableNodes.length && maxTableCols > 0) {
    cursorX = tableX + maxTableCols * tableColW - COL_GAP + LAYER_GAP;
  } else if (hasSchemaColumn) {
    cursorX = schemaX + square + LAYER_GAP;
  } else if (dsNodes.length) {
    cursorX = dsX + square + LAYER_GAP;
  } else {
    cursorX = MARGIN;
  }

  // Downstream wrap: one vertical column, top-aligned with the DS cluster.
  // Fill downward until the next node would pass the wrap limit, then start
  // column 2. Limit = max(canvas viewport, DS/schema/table cluster height) so
  // ontologies run the full height beside the table blocks (ref. experiment v2)
  // — not a fixed grid like tables, and not capped to a short viewport slice.
  const clusterBottom =
    dsNodes.length || schemaNodes.length || tableNodes.length
      ? runningY - BLOCK_GAP
      : downstreamStartY;
  const wrapBottomY = Math.max(viewportHeight - MARGIN, clusterBottom);

  DOWNSTREAM_TYPES.forEach((type) => {
    const layerNodes = orderByRank(byType(type));
    if (!layerNodes.length) return;

    const nodeW = Math.max(
      ...layerNodes.map((n) => dims.get(n.id)?.width ?? KG_TABLE_NODE_WIDTH_PX),
    );
    const colStride = nodeW + COL_GAP;

    let subCol = 0;
    let currentY = downstreamStartY;
    let maxSubCol = 0;

    layerNodes.forEach((node) => {
      const nodeH = downstreamLayoutHeight(node);

      if (currentY + nodeH > wrapBottomY && currentY > downstreamStartY) {
        subCol += 1;
        currentY = downstreamStartY;
      }

      positions.set(node.id, {
        x: cursorX + subCol * colStride,
        y: currentY,
      });

      maxSubCol = Math.max(maxSubCol, subCol);
      currentY += nodeH + ROW_GAP;
    });

    cursorX += (maxSubCol + 1) * colStride - COL_GAP + LAYER_GAP;
  });

  // ── Commit positions ───────────────────────────────────────────────────────
  return nodes.map((node) => {
    const pos = positions.get(node.id) ?? {
      x: 0,
      y: orderRank.get(node.id) ?? 0,
    };
    return {
      ...node,
      position: { x: pos.x, y: pos.y },
      sourcePosition,
      targetPosition,
    } as KgFlowNode;
  });
};
